/* eslint react-hooks/exhaustive-deps: 0 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateTaxiCompanyConfigurations } from '../../../../../actions/generalAction';
import { getReservation } from '../../../../../services/taxi/GetReservation';
import { getTaxiCompanyConfiguration } from '../../../../../services/taxi/GetTaxiMetaInfo';
import { TaxiCompanyConfigurations } from '../../../../../services/taxi/GetTaxiMetaInfo/model/taxiCompanyConf';
import { ReservationResponse } from '../../../../../services/taxi/Reservation/ReservationResponse';
import Environment from '../../../../../utils/environment';
import MockResponse from '../../../../../services/taxi/GetReservation/mock/mockReservationResponse.json';
import { convertStringToTaxiCompany, TaxiCompanyList } from '../../../../../services/taxi/GetDirectionAndFare/models/getFareSamplesParams';

/**
 * タクシーの仮予約情報に関するカスタムフック。
 * @returns code 仮予約情報を参照するためのコード。
 * @returns token 仮予約情報を参照するためのトークン。
 * @returns isLoadingReservation 仮予約情報を取得中であるかのフラグ。
 * @returns hasReservation 仮予約情報が存在するかを表すフラグ。
 * @returns tmpReservation 仮予約情報。
 */
export function useTemporaryReservation() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  /** URLパラメーター。 */
  const searchParams = new URLSearchParams(location.search);
  /** 仮予約情報を参照するためのコードをURLパラメーターから取得する関数 */
  const getCode = () => {
    if (searchParams.has("code")) {
       /** URLパラメーター「code」の値。 */
      const value = searchParams.get("code");
      // nullではない場合のみそのまま値を、nullの場合は空文字を返す。
      return (value !== null) ? value : '';
    } else {
      return '';
    }
  }

  /** 仮予約情報を参照するためのトークンをURLパラメーターから取得する関数。 */
  const getToken = () => {
    if (searchParams.has("token")) {
      /** URLパラメーター「token」の値。 */
      const value = searchParams.get("token");
      // nullではない場合のみそのまま値を、nullの場合は空文字を返す。
      return  (value !== null) ? value : '';
    } else {
      return '';
    }
  }

  /** 言語情報を取得する */
  const getLang = (): string => {
    if (searchParams.has("language")) {
      /** URLパラメーター「token」の値。 */
      const value = searchParams.get("language");
      // nullではない場合のみそのまま値を、nullの場合はjaを返す。
      return  (value !== null) ? value : 'ja';
    } else {
      return 'ja';
    }
  }

  /** 仮予約時に発行したコード。 */
  const code = getCode();
  /** 仮予約時に発行したトークン。 */
  const token = getToken();
  /** 言語情報を取得する */
  const language = getLang();

  React.useEffect(()=>{
    /** 言語情報をセットする */
    switch(language) {
      case 'en':
        i18n.changeLanguage('en');
        break;
      case 'ja':
      default:
        i18n.changeLanguage('ja');
        break;
    }
  }, [])


  const [isLoadingReservation, switchLoadingReservation] = React.useState(false);
  /** 仮予約情報が存在するかを表すフラグ。 */
  const [hasReservation, setHasReservation] = React.useState(true);
  /** 仮予約情報。 */
  const [tmpReservation, setTmpReservation] = React.useState<ReservationResponse>();
  /** タクシー会社ごとのメタデータ */
  const [ taxiCompanyConf, setTaxiCompanyConf ] = React.useState<TaxiCompanyConfigurations|null>(null);
  /** デバッグモードかどうかのフラグ。URLパラメーターにdebug=trueが存在する場合はtrue。 */
  const [ isDebug, setIsDebug ] = React.useState(false);

  /**
   * 仮予約情報を参照するAPIにリクエストする。
   * @param code 仮予約時に発行したコード。
   * @param token 仮予約時に発行したトークン。
   * @param language 言語情報
   */
  const getTemporaryReservation = React.useCallback(async(code: string, token: string, language: string) => {
    return getReservation({code, token, language});
  }, []);

  /**
   * タクシーメタ情報を取得後の後処理
   */
  async function getTaxiCompanyConf(companyCode: TaxiCompanyList) {
    const params = {
      companyCode: companyCode || undefined,
      key: 'company_configuration',
      language: i18n.language
    }
    try {
      // タクシー会社メタ情報を取得する
      const response: {
          type: string;
          body: TaxiCompanyConfigurations | null;
      } = await getTaxiCompanyConfiguration(params);
      // 情報が取れないときには何も処理をしない
      if(!response.body) {
        return;
      }
      const getTaxiCompanyConf: TaxiCompanyConfigurations = response.body;
      // Redux上にタクシーメタ情報を保存する
      dispatch(updateTaxiCompanyConfigurations(getTaxiCompanyConf));
      setTaxiCompanyConf(getTaxiCompanyConf);
    }
    catch {
      // 予約情報が存在するフラグをfalseにする。
      setHasReservation(false);
    }
    // 読込中フラグをオフにする。
    switchLoadingReservation(false);
  }

  // 画面読み込み時に予約コード・予約トークンの存在チェックをする処理。
  React.useEffect(() => {
    // コードとトークンが未設定の場合は、予約なしとして扱う。
    if (!code || !token || code === null || token === null) {
      setHasReservation(false);
    }
  }, [code, token]);

  // 仮予約情報を参照する処理。
  React.useEffect(() => {
    // コードとトークンが設定されている場合のみ、予約情報を参照する。
    if (code && token) {
      // 読込中フラグをオンにする。
      switchLoadingReservation(true);
      (async () => {
        // dev環境かつtokenにdebugが指定された場合には、モックのレスポンスを返す。
        if (Environment.getEnvironmentType() !== Environment.PRODUCTION && token === 'debug') {
          // code=company として取り扱い、codeに応じたモックのレスポンスを返す。
          // 予約情報が存在するフラグをtrueにする。
          setHasReservation(true);
          // デバッグモードをオンにする。
          setIsDebug(true);
          const mockResponse = MockResponse as unknown as ReservationResponse;
          mockResponse.companyCode = convertStringToTaxiCompany(code) || '';
          // URLパラメーターにdebugRideDateが存在する場合は、モックのレスポンスの乗車日時をdebugRideDateの値に置き換える。
          if(searchParams.get('debugRideDate')) {
            mockResponse.rideDatetime = searchParams.get('debugRideDate') || mockResponse.rideDatetime;
          }
          // URLパラメーターにdebugAirportが存在する場合は、モックのレスポンスの空港コードをdebugAirportの値に置き換える。また、発着地の住所を「ダミー空港: ○○」に置き換える。
          if(searchParams.get('debugAirport')) {
            mockResponse.airportInfo.letter = searchParams.get('debugAirport') || mockResponse.airportInfo.letter;
            mockResponse.originLabel = `ダミー空港: ${mockResponse.airportInfo.letter}`;
          }
          // 予約情報をセットする。
          setTmpReservation(mockResponse);
          const companyCode = convertStringToTaxiCompany(code);
          await getTaxiCompanyConf(companyCode as TaxiCompanyList);
          return;
        }
        // 仮予約情報を参照するAPIにリクエストする。
        const getTemporaryReservationResponse = await getTemporaryReservation(code, token, language);

        // 正常にレスポンスが返却された場合。
        if (getTemporaryReservationResponse !== null && getTemporaryReservationResponse.status === 200) {
          // 予約情報が存在するフラグをtrueにする。
          setHasReservation(true);
          // 予約情報をセットする。
          setTmpReservation(getTemporaryReservationResponse.data as ReservationResponse);
        } else {
          // 予約情報が存在するフラグをfalseにする。
          setHasReservation(false);
        }
        // タクシー会社メタ情報を取得する
        await getTaxiCompanyConf(getTemporaryReservationResponse?.data?.companyCode);
      })()

    }
  }, [code, token, getTemporaryReservation]);

  return {
    code, token,
    isLoadingReservation,
    hasReservation,
    tmpReservation,
    taxiCompanyConf,
    isDebug
  }
}
